// # Trends Analysis Component for Net Revenue Dashboard
import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ComposedChart
} from 'recharts';
import { 
  FaChartLine, 
  FaProjectDiagram,
  FaCalendarAlt,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaPercentage,
  FaMoneyBillWave,
  FaChartArea,
  FaFilter,
  FaExchangeAlt
} from 'react-icons/fa';
import './trends_analysis.css';

// # Add default data structure
const defaultAnalyticsData = {
  revenueOverTime: [],
  monthlyGrowth: [],
  rawPayments: [],
  stats: {
    totalRevenue: 0,
    totalNetRevenue: 0,
    totalBudget: 0,
    growthRate: 0,
    projectedAnnual: 0
  }
};

const TrendsAnalysis = ({ analyticsData = defaultAnalyticsData }) => {
  // # State for time period filtering
  const [timePeriod, setTimePeriod] = useState('all');
  const [filteredData, setFilteredData] = useState({
    revenueOverTime: [],
    monthlyGrowth: [],
    paymentsByMonth: {}
  });
  
  // # Calculate trend-specific stats
  const [trendStats, setTrendStats] = useState({
    growthRate: 0,
    projectedAnnual: 0,
    averageMonthlyGrowth: 0,
    volatility: 0,
    forecastNextMonth: 0,
    forecastNextQuarter: 0,
    totalRevenue: 0,
    totalNetRevenue: 0,
    totalBudget: 0
  });
  
  // # Process raw payments data to get monthly trends
  useEffect(() => {
    if (!analyticsData?.rawPayments?.length && !analyticsData?.revenueOverTime?.length) return;
    
    // # Get current date for filtering
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    // # Determine if we have raw payments data or pre-processed revenue data
    const hasRawPayments = analyticsData?.rawPayments?.length > 0;
    
    let monthlyData = {};
    
    if (hasRawPayments) {
      // # Process raw payments data into monthly aggregates
      analyticsData.rawPayments.forEach(payment => {
        const paymentDate = new Date(payment.timestamp);
        const monthKey = `${paymentDate.getFullYear()}-${String(paymentDate.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {
            month: monthKey,
            revenue: 0,
            netRevenue: 0,
            budget: 0,
            count: 0
          };
        }
        
        // # Add payment data to monthly totals
        monthlyData[monthKey].revenue += payment.egp_total || 0;
        monthlyData[monthKey].netRevenue += payment.egp_net || 0;
        monthlyData[monthKey].budget += payment.budget_dvd_amount || 0;
        monthlyData[monthKey].count++;
      });
    } else {
      // # Use pre-processed revenue data
      analyticsData.revenueOverTime.forEach(entry => {
        monthlyData[entry.month] = {
          month: entry.month,
          revenue: entry.revenue || 0,
          netRevenue: entry.netRevenue || 0,
          budget: entry.budget || 0,
          count: 0 // # We don't have count in pre-processed data
        };
      });
    }
    
    // # Convert to array and sort by month
    let revenueOverTime = Object.values(monthlyData).sort((a, b) => 
      a.month.localeCompare(b.month)
    );
    
    // # Apply time period filtering
    if (timePeriod === '3months') {
      // # Filter last 3 months
      const threeMonthsAgo = new Date(currentYear, currentMonth - 3, 1);
      revenueOverTime = revenueOverTime.filter(item => {
        const [year, month] = item.month.split('-').map(Number);
        const itemDate = new Date(year, month - 1, 1);
        return itemDate >= threeMonthsAgo;
      });
    } else if (timePeriod === '6months') {
      // # Filter last 6 months
      const sixMonthsAgo = new Date(currentYear, currentMonth - 6, 1);
      revenueOverTime = revenueOverTime.filter(item => {
        const [year, month] = item.month.split('-').map(Number);
        const itemDate = new Date(year, month - 1, 1);
        return itemDate >= sixMonthsAgo;
      });
    } else if (timePeriod === '1year') {
      // # Filter last year
      const oneYearAgo = new Date(currentYear - 1, currentMonth, 1);
      revenueOverTime = revenueOverTime.filter(item => {
        const [year, month] = item.month.split('-').map(Number);
        const itemDate = new Date(year, month - 1, 1);
        return itemDate >= oneYearAgo;
      });
    }
    
    // # Calculate monthly growth rates
    const monthlyGrowth = [];
    for (let i = 1; i < revenueOverTime.length; i++) {
      const prevMonth = revenueOverTime[i - 1];
      const currentMonth = revenueOverTime[i];
      const prevRevenue = prevMonth.revenue || 0;
      const currentRevenue = currentMonth.revenue || 0;
      
      const growth = prevRevenue > 0 
        ? ((currentRevenue - prevRevenue) / prevRevenue) * 100 
        : 0;

      monthlyGrowth.push({
        month: currentMonth.month,
        growth: growth,
        previousRevenue: prevRevenue,
        currentRevenue: currentRevenue,
        netGrowth: prevMonth.netRevenue > 0 
          ? ((currentMonth.netRevenue - prevMonth.netRevenue) / prevMonth.netRevenue) * 100 
          : 0
      });
    }
    
    // # Update filtered data
    setFilteredData({
      revenueOverTime,
      monthlyGrowth,
      paymentsByMonth: monthlyData
    });
    
    // # Calculate trend stats
    if (revenueOverTime.length > 0) {
      // # Calculate totals for the filtered period
      const totalRevenue = revenueOverTime.reduce((sum, month) => sum + month.revenue, 0);
      const totalNetRevenue = revenueOverTime.reduce((sum, month) => sum + month.netRevenue, 0);
      const totalBudget = revenueOverTime.reduce((sum, month) => sum + month.budget, 0);
      
      // # Calculate average monthly revenue
      const avgMonthlyRevenue = totalRevenue / revenueOverTime.length;
      
      // # Calculate growth rates if we have enough data
      let averageMonthlyGrowth = 0;
      let volatility = 0;
      let overallGrowthRate = 0;
      
      if (monthlyGrowth.length > 0) {
        // # Calculate average monthly growth rate
        const growthRates = monthlyGrowth.map(item => item.growth);
        averageMonthlyGrowth = growthRates.reduce((sum, rate) => sum + rate, 0) / growthRates.length;
        
        // # Calculate volatility (standard deviation of growth rates)
        const squaredDiffs = growthRates.map(rate => Math.pow(rate - averageMonthlyGrowth, 2));
        const avgSquaredDiff = squaredDiffs.reduce((sum, diff) => sum + diff, 0) / squaredDiffs.length;
        volatility = Math.sqrt(avgSquaredDiff);
        
        // # Calculate overall growth rate (comparing first and last month)
        const firstMonth = revenueOverTime[0];
        const lastMonth = revenueOverTime[revenueOverTime.length - 1];
        overallGrowthRate = firstMonth.revenue > 0 
          ? ((lastMonth.revenue - firstMonth.revenue) / firstMonth.revenue) * 100 
          : 0;
      }
      
      // # Calculate forecasts
      const lastMonth = revenueOverTime[revenueOverTime.length - 1];
      const lastMonthRevenue = lastMonth.revenue;
      
      // # Calculate forecasts using compound growth formula
      // # Future Value = Present Value * (1 + Growth Rate)^n
      const monthlyGrowthFactor = 1 + (averageMonthlyGrowth / 100);
      const forecastNextMonth = lastMonthRevenue * monthlyGrowthFactor;
      const forecastNextQuarter = lastMonthRevenue * Math.pow(monthlyGrowthFactor, 3);
      
      // # Calculate projected annual revenue
      // # If we have less than 12 months of data, project based on average monthly revenue
      const projectedAnnual = revenueOverTime.length >= 12 
        ? totalRevenue 
        : avgMonthlyRevenue * 12;
      
      // # Update trend stats
      setTrendStats({
        growthRate: overallGrowthRate,
        projectedAnnual: projectedAnnual,
        averageMonthlyGrowth: averageMonthlyGrowth,
        volatility: volatility,
        forecastNextMonth: forecastNextMonth,
        forecastNextQuarter: forecastNextQuarter,
        totalRevenue: totalRevenue,
        totalNetRevenue: totalNetRevenue,
        totalBudget: totalBudget
      });
    } else {
      // # No data available
      setTrendStats({
        growthRate: 0,
        projectedAnnual: 0,
        averageMonthlyGrowth: 0,
        volatility: 0,
        forecastNextMonth: 0,
        forecastNextQuarter: 0,
        totalRevenue: 0,
        totalNetRevenue: 0,
        totalBudget: 0
      });
    }
    
  }, [analyticsData, timePeriod]);
  
  // # Handle time period selection
  const handlePeriodChange = (period) => {
    setTimePeriod(period);
  };
  
  // # Format currency values
  const formatCurrency = (value) => {
    return `E£ ${value.toLocaleString()}`;
  };
  
  // # Get trend indicator component based on value
  const getTrendIndicator = (value) => {
    if (value > 0) {
      return <span className="fnc_trnd_indicator fnc_trnd_indicator-up"><FaArrowUp /> {value.toFixed(2)}%</span>;
    } else if (value < 0) {
      return <span className="fnc_trnd_indicator fnc_trnd_indicator-down"><FaArrowDown /> {Math.abs(value).toFixed(2)}%</span>;
    } else {
      return <span className="fnc_trnd_indicator fnc_trnd_indicator-neutral"><FaMinus /> 0.00%</span>;
    }
  };
  
  // # If no analytics data is available
  if ((!analyticsData?.rawPayments || analyticsData.rawPayments.length === 0) && 
      (!analyticsData?.revenueOverTime || analyticsData.revenueOverTime.length === 0)) {
    return <div className="fnc_trnd_loading">No data available for trends analysis</div>;
  }

  return (
    <div className="fnc_trnd_container">
      {/* Trends Stats Overview */}
      <div className="fnc_trnd_stats-grid">
        <div className="fnc_trnd_stat-card">
          <FaChartLine className="fnc_trnd_stat-icon" />
          <h3>Overall Growth Rate</h3>
          <p className="fnc_trnd_stat-value">
            {trendStats.growthRate.toFixed(2)}%
          </p>
          {getTrendIndicator(trendStats.growthRate)}
        </div>
        <div className="fnc_trnd_stat-card">
          <FaProjectDiagram className="fnc_trnd_stat-icon" />
          <h3>Projected Annual</h3>
          <p className="fnc_trnd_stat-value">
            {formatCurrency(trendStats.projectedAnnual)}
          </p>
        </div>
        <div className="fnc_trnd_stat-card">
          <FaPercentage className="fnc_trnd_stat-icon" />
          <h3>Avg Monthly Growth</h3>
          <p className="fnc_trnd_stat-value">
            {trendStats.averageMonthlyGrowth.toFixed(2)}%
          </p>
          {getTrendIndicator(trendStats.averageMonthlyGrowth)}
        </div>
        <div className="fnc_trnd_stat-card">
          <FaChartArea className="fnc_trnd_stat-icon" />
          <h3>Growth Volatility</h3>
          <p className="fnc_trnd_stat-value">
            {trendStats.volatility.toFixed(2)}%
          </p>
        </div>
      </div>
      
      {/* Time Period Controls */}
      <div className="fnc_trnd_period-controls">
        <button 
          className={`fnc_trnd_period-button ${timePeriod === 'all' ? 'active' : ''}`}
          onClick={() => handlePeriodChange('all')}
        >
          All Time
        </button>
        <button 
          className={`fnc_trnd_period-button ${timePeriod === '3months' ? 'active' : ''}`}
          onClick={() => handlePeriodChange('3months')}
        >
          Last 3 Months
        </button>
        <button 
          className={`fnc_trnd_period-button ${timePeriod === '6months' ? 'active' : ''}`}
          onClick={() => handlePeriodChange('6months')}
        >
          Last 6 Months
        </button>
        <button 
          className={`fnc_trnd_period-button ${timePeriod === '1year' ? 'active' : ''}`}
          onClick={() => handlePeriodChange('1year')}
        >
          Last Year
        </button>
      </div>
      
      {/* Revenue Breakdown */}
      <div className="fnc_trnd_breakdown">
        <h3>Revenue Breakdown for Selected Period</h3>
        <div className="fnc_trnd_breakdown-grid">
          <div className="fnc_trnd_breakdown-item">
            <h4>Total Revenue</h4>
            <p className="fnc_trnd_breakdown-value">{formatCurrency(trendStats.totalRevenue)}</p>
          </div>
          <div className="fnc_trnd_breakdown-item">
            <h4>Net Revenue</h4>
            <p className="fnc_trnd_breakdown-value">{formatCurrency(trendStats.totalNetRevenue)}</p>
          </div>
          <div className="fnc_trnd_breakdown-item">
            <h4>Budget Allocation</h4>
            <p className="fnc_trnd_breakdown-value">{formatCurrency(trendStats.totalBudget)}</p>
          </div>
          <div className="fnc_trnd_breakdown-item">
            <h4>Budget Percentage</h4>
            <p className="fnc_trnd_breakdown-value">
              {trendStats.totalRevenue > 0 
                ? ((trendStats.totalBudget / trendStats.totalRevenue) * 100).toFixed(2) 
                : 0}%
            </p>
          </div>
        </div>
      </div>
      
      {/* Revenue Forecast Section */}
      <div className="fnc_trnd_forecast">
        <h3>Revenue Forecast</h3>
        <div className="fnc_trnd_forecast-grid">
          <div className="fnc_trnd_forecast-item">
            <h4>Next Month</h4>
            <p className="fnc_trnd_forecast-value">{formatCurrency(trendStats.forecastNextMonth)}</p>
            {getTrendIndicator(trendStats.averageMonthlyGrowth)}
          </div>
          <div className="fnc_trnd_forecast-item">
            <h4>Next Quarter</h4>
            <p className="fnc_trnd_forecast-value">{formatCurrency(trendStats.forecastNextQuarter)}</p>
          </div>
          <div className="fnc_trnd_forecast-item">
            <h4>Annual Projection</h4>
            <p className="fnc_trnd_forecast-value">{formatCurrency(trendStats.projectedAnnual)}</p>
          </div>
        </div>
      </div>
      
      {/* Monthly Growth Chart */}
      <div className="fnc_trnd_chart-container">
        <h3>Monthly Growth Rate</h3>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={filteredData.monthlyGrowth}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip 
              formatter={(value) => `${value.toFixed(2)}%`}
              labelFormatter={(label) => `Month: ${label}`}
            />
            <Legend />
            <Bar 
              dataKey="growth" 
              fill="#8884d8" 
              name="Total Revenue Growth (%)" 
            />
            <Line 
              type="monotone" 
              dataKey="netGrowth" 
              stroke="#82ca9d" 
              name="Net Revenue Growth (%)" 
              dot={{ r: 4 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      
      {/* Revenue Trend Chart */}
      <div className="fnc_trnd_chart-container">
        <h3>Revenue Trend</h3>
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={filteredData.revenueOverTime}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip formatter={(value) => formatCurrency(value)} />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="revenue" 
              stroke="#8884d8" 
              fill="#8884d8" 
              fillOpacity={0.3}
              name="Total Revenue" 
            />
            <Area 
              type="monotone" 
              dataKey="netRevenue" 
              stroke="#82ca9d" 
              fill="#82ca9d" 
              fillOpacity={0.3}
              name="Net Revenue" 
            />
            <Area 
              type="monotone" 
              dataKey="budget" 
              stroke="#ffc658" 
              fill="#ffc658" 
              fillOpacity={0.3}
              name="Budget" 
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      {/* Revenue Composition Chart */}
      <div className="fnc_trnd_chart-container">
        <h3>Revenue Composition</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={filteredData.revenueOverTime}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip formatter={(value) => formatCurrency(value)} />
            <Legend />
            <Bar dataKey="netRevenue" stackId="a" fill="#82ca9d" name="Net Revenue" />
            <Bar dataKey="budget" stackId="a" fill="#ffc658" name="Budget" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Monthly Data Table */}
      <div className="fnc_trnd_section">
        <h3>Monthly Revenue Data</h3>
        <div className="fnc_trnd_table-container">
          <table className="fnc_trnd_table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Total Revenue</th>
                <th>Net Revenue</th>
                <th>Budget</th>
                <th>Growth</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.revenueOverTime.map((item, index) => {
                // Find corresponding growth data
                const growthData = filteredData.monthlyGrowth.find(g => g.month === item.month);
                const growthRate = growthData ? growthData.growth : 0;
                
                return (
                  <tr key={index}>
                    <td>{item.month}</td>
                    <td>{formatCurrency(item.revenue)}</td>
                    <td>{formatCurrency(item.netRevenue)}</td>
                    <td>{formatCurrency(item.budget)}</td>
                    <td>{getTrendIndicator(growthRate)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TrendsAnalysis; 